import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { identify } from '../lib/amplitude';
import { fetchMe } from '../queries/account';
import { useOrg } from './OrgProvider';

/**
 * Who the analytics are about.
 *
 * Firebase says a browser is signed in; GET /v1/me says which Slate account
 * that is, and the Slate account is what every client reports against. So the
 * identify waits for /v1/me rather than the Firebase session, and sends the
 * Postgres uuid, never the Firebase uid.
 *
 * Mounted once, above the router, so a share page opened by a signed-in
 * person identifies them the same way the library does. It renders nothing;
 * the query shares its key with the rest of the app, so this costs no extra
 * request on any page that already asks who you are.
 */
export function AnalyticsIdentity(): null {
  const { user } = useAuth();
  const { org } = useOrg();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    enabled: Boolean(user),
    staleTime: 60_000,
  });

  const account = me.data ?? null;
  const userId = account?.id ?? null;
  const email = account?.email ?? null;
  const name = account?.name ?? null;
  const plan: 'free' | 'premium' = account?.paidSeat ? 'premium' : 'free';
  // A seat bought, or one given to the team. A team whose seats are all on us
  // is the only way a paid seat is not a paid seat.
  const planSource: 'seat' | 'comp' | 'none' = !account?.paidSeat
    ? 'none'
    : org?.seats && org.seats.paid === 0 && org.seats.comped > 0
      ? 'comp'
      : 'seat';
  const teams = account?.memberships.length ?? 0;

  useEffect(() => {
    if (!userId) return;
    identify({ userId, email, name, plan, planSource, teams });
  }, [email, name, plan, planSource, teams, userId]);

  return null;
}
